import { useState } from "react";
import InternalPage from "../../../components/layout/internal/InternalPage";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import {
  selectEntryByKey,
  fetchFormEntries,
} from "../../../store/slices/formEntriesSlice";
import { checkInRegistrants } from "../../../services/apiService";
import { useNotification } from "../../../context/NotificationContext";
import { formMappings } from "../../../config/formMappings";
import { getRegistrantsContainer } from "../../../utils/utils";

export default function CheckInPage() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { registrantKey } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedRegistrantKeys, setSelectedRegistrantKeys] = useState<
    string[]
  >([]);

  const entry = useSelector((state: RootState) =>
    selectEntryByKey(state, registrantKey || "")
  );
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );

  // Get form mapping to access dynamic field keys
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const checkedInFieldKey = formMapping?.fieldMappings?.["Checked In"];
  const firstNameFieldKey = formMapping?.fieldMappings?.firstName;
  const lastNameFieldKey = formMapping?.fieldMappings?.lastName;
  const emailFieldKey = formMapping?.fieldMappings?.email;

  // Get registrants container using utility function (handles both v1 and v2)
  const registrantsContainer = entry
    ? getRegistrantsContainer(entry.meta)
    : null;

  const updateEntries = async () => {
    const formMapping =
      formMappings[activeFormName as keyof typeof formMappings];
    if (formMapping) {
      await dispatch(fetchFormEntries(formMapping.formId));
    }
  };

  const handleRegistrantClick = (key: string) => {
    const sanitizedKey = key.startsWith("i") ? key.slice(1) : key;

    setSelectedRegistrantKeys((prevKeys) =>
      prevKeys.includes(sanitizedKey)
        ? prevKeys.filter((k) => k !== sanitizedKey)
        : [...prevKeys, sanitizedKey]
    );
  };

  const handleSelectAll = () => {
    if (registrantsContainer) {
      const allKeys = Object.keys(registrantsContainer)
        .filter((key) => key !== "form")
        .map((key) => (key.startsWith("i") ? key.slice(1) : key));

      setSelectedRegistrantKeys(
        selectedRegistrantKeys.length === allKeys.length ? [] : allKeys
      );
    }
  };

  const handleCheckIn = async () => {
    if (entry && selectedRegistrantKeys.length > 0) {
      try {
        await checkInRegistrants(
          selectedRegistrantKeys,
          entry.id,
          activeFormName
        );
        showNotification({
          title: "Successfully Checked-In!",
          message: `Successfully checked-in ${selectedRegistrantKeys.length} guests.`,
          type: "success",
        });
        await updateEntries();
        navigate(-1);
      } catch (error) {
        showNotification({
          title: "Checked-In Failed",
          message: `Failed to check in ${selectedRegistrantKeys.length} guests.`,
          type: "error",
        });
        console.error("Failed to check in registrants:", error);
      }
    } else {
      showNotification({
        title: "Nothing Selected",
        message: `No registrants selected for check-in.`,
        type: "info",
      });
    }
  };

  return (
    <InternalPage>
      {entry ? (
        <>
          <section aria-labelledby="general-information-title">
            <div className="mb-5 md:flex md:items-center md:justify-between md:space-x-5">
              <div className="flex items-center space-x-5">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Check-In Registrants
                  </h1>
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
                >
                  {registrantsContainer &&
                  selectedRegistrantKeys.length ===
                    Object.keys(registrantsContainer).length - 1
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {registrantsContainer &&
              Object.entries(registrantsContainer)
                .filter(([key]) => key !== "form")
                .map(([key, registrant], registrantIdx) => {
                  const sanitizedKey = key.startsWith("i") ? key.slice(1) : key;
                  const isSelected =
                    selectedRegistrantKeys.includes(sanitizedKey);
                  const registrantData = registrant as any;

                  return (
                    <div
                      key={registrantIdx}
                      onClick={() => handleRegistrantClick(key)}
                      className={`relative flex items-center space-x-3 rounded-lg border px-6 py-5 shadow-sm transition-all cursor-pointer ${
                        isSelected
                          ? "border-primary bg-blue-50"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => e.stopPropagation()} // Prevent click from triggering div onClick
                        className="mr-3 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {`${
                            firstNameFieldKey
                              ? registrantData[firstNameFieldKey] || ""
                              : ""
                          } ${
                            lastNameFieldKey
                              ? registrantData[lastNameFieldKey] || ""
                              : ""
                          }`}
                          {checkedInFieldKey &&
                          registrantData[checkedInFieldKey] === "true" ? (
                            <span className="ml-3 inline-flex float-right items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              Checked-In
                            </span>
                          ) : (
                            <span className="ml-3 inline-flex float-right items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                              Not Checked-In
                            </span>
                          )}
                        </p>
                        <p className="truncate text-sm text-gray-500">
                          {emailFieldKey
                            ? registrantData[emailFieldKey] || ""
                            : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
          </div>
          <div>
            <button
              onClick={handleCheckIn}
              disabled={selectedRegistrantKeys.length === 0}
              className="mt-5 w-full items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary disabled:bg-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
            >
              Check-In{" "}
              {selectedRegistrantKeys.length > 0 &&
                `${selectedRegistrantKeys.length} guests`}
            </button>
          </div>
        </>
      ) : (
        <p>No entry found with the provided key.</p>
      )}
    </InternalPage>
  );
}
